import {FetchService} from "dc-services/fetch.service";
import {MustacheService} from "dc-services/mustache.service";
import {TextconstantService} from "dc-services/textconstant.service";
import {closeModal, showModal} from "cronus-theme/default/global/js/modal";
import {initInputBody} from "cronus-theme/default/global/js/inputfile";

/**
 * Ordnernamen der Gewaehrleistungs-Dateien auf die Container im Detail-Template abbilden.
 * Die Typen entsprechen den Unterordnern in userdata/warranty_uploads/{customerNo}/ —
 * teils aus WarrantyHandler::ALLOWED_UPLOAD_TYPES, teils von BC angelegt (warranty_report).
 */
const FILE_TYPE_CONTAINERS = {
    invoice: "invoiceFiles",
    purchase_document: "purchaseFiles",
    pictures: "imageFiles",
    warranties: "warrantyFiles",
    warranty_report: "warrantyFiles",
    report: "reportFiles"
};

export function warranties() {
    getAllWarranties(1);
}

function getPaginationTemplate(page, maxEntries, limit) {
    if(maxEntries < limit) {
        return "";
    }

    const perPage = Math.max(1, Number(limit) || 1);
    const totalEntries = Math.max(0, Number(maxEntries) || 0);
    const totalPages = Math.max(1, Math.ceil(totalEntries / perPage));
    const current = Math.min(totalPages, Math.max(1, Number(page) || 1));

    const parts = [];

    const pushPage = (p, label, active = false) => {
        const cls = `js-warrantyPagination pagination__item${active ? ' pagination__item--active' : ''}`;
        const aria = active ? 'aria-current="true"' : `aria-label="Goto Page ${p}"`;
        parts.push(`<li><a class="${cls}" href="javascript:void(0);" ${aria} data-page="${p}">${label}</a></li>`);
    };
    const pushSep = () => parts.push('<span class="pagination__item pagination__item--seperator">...</span>');

    // Prev arrow
    if (current > 1) {
        parts.push(`<li><a class="js-warrantyPagination pagination__item pagination__item--prev" href="javascript:void(0);" aria-label="Previous Page" data-page="${current - 1}"><i class="icon icon-angle-left" aria-hidden="true"></i></a></li>`);
    }

    // First page
    pushPage(1, '1', current === 1);

    // Middle window (two previous and two next)
    const start = Math.max(2, current - 2);
    const end = Math.min(totalPages - 1, current + 2);

    if (start > 2) pushSep();
    for (let p = start; p <= end; p++) {
        pushPage(p, String(p), p === current);
    }
    if (end < totalPages - 1) pushSep();

    // Last page
    if (totalPages > 1) {
        pushPage(totalPages, String(totalPages), current === totalPages);
    }

    // Next arrow
    if (current < totalPages) {
        parts.push(`<li><a class="js-warrantyPagination pagination__item pagination__item--prev" href="javascript:void(0);" aria-label="Next Page" data-page="${current + 1}"><i class="icon icon-angle-right" aria-hidden="true"></i></a></li>`);
    }

    return `<nav role="navigation" aria-label="Pagination"><ul class="pagination">${parts.join('')}</ul></nav>`;
}

function formatDateEU(input) {
    if (!input) return '';
    // Wenn bereits im Format DD.MM.YYYY, zurückgeben
    if (/^\d{2}\.\d{2}\.\d{4}(\s+\d{2}:\d{2}(:\d{2})?)?$/.test(input)) {
        return input;
    }

    const d = new Date(input);
    if (isNaN(d.getTime())) {
        return String(input);
    }

    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();

    // Falls Uhrzeit relevant sein sollte, kann sie hier ergänzt werden
    // const hh = String(d.getHours()).padStart(2, '0');
    // const mi = String(d.getMinutes()).padStart(2, '0');
    // return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
    return `${dd}.${mm}.${yyyy}`;
}

function mapStateToLabel(stateVal) {
    // Normalize to number when possible
    let num;
    if (typeof stateVal === 'number') {
        num = stateVal;
    } else if (typeof stateVal === 'string' && stateVal.trim() !== '') {
        const parsed = Number(stateVal);
        num = Number.isNaN(parsed) ? stateVal : parsed;
    } else {
        num = stateVal;
    }

    switch (num) {
        case 0:
            return GLOBALS.tc.open;
        case 1:
            // Obsolete in BC, kept for records that still have this state
            return GLOBALS.tc.progress;
        case 10:
            return GLOBALS.tc.at_manufacturer;
        case 20:
            return GLOBALS.tc.disassembly_confirmation;
        case 30:
            return GLOBALS.tc.done;
        case 40:
            return GLOBALS.tc.in_transit;
        default:
            // Fallback: unbekannter Status bleibt wie geliefert
            return String(stateVal ?? '');
    }
}

async function getAllWarranties(page, showSuccess = false, ticketId = false) {
    const wrapperElement = document.getElementById("warrantiesWrapper");

    const loader = document.getElementById("loadingList");

    if(wrapperElement && loader) {
        wrapperElement.innerHTML = "";
        loader.style.display = "block";

        const limit = 10;

        const response = await FetchService.get('/em/warranty/getAllForCustomer/' + page + '?limit=' + limit);
        await TextconstantService.preloadTextConstants('bet')
        const tcs = TextconstantService.getAllTextConstants();

        if (response.success) {
            const lines = Array.isArray(response.data)
                ? [...response.data]
                    .sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0))
                    .map((row) => ({
                        ...row,
                        creation_date: formatDateEU(row?.creation_date),
                        state_text: mapStateToLabel(row?.state)
                    }))
                : [];

            let maxEntries = 500;
            const entriesResponse = await FetchService.get('/em/warranty/getWarrantyCount/');
            if(entriesResponse.success) {
                maxEntries = entriesResponse.data;
            }

            const renderedTemplate = await MustacheService.getRenderedTemplate('/layout/frontend/beturbo/templates/warranties', 'warranties_list.mustache', {
                lines,
                showSuccess: showSuccess,
                ticketId: ticketId,
                tc:tcs,
                pagination: getPaginationTemplate(page, maxEntries, limit)
            })
            wrapperElement.append(...renderedTemplate);
            loader.style.display = "none";

            const paginationItems = document.querySelectorAll(".js-warrantyPagination");
            for(const item of paginationItems) {
                item.addEventListener("click", function (e) {
                    e.preventDefault();
                    const page = this.dataset.page;
                    getAllWarranties(page);
                });
            }

            getSingleWarranty();

            // Neuer Gewährleistungsfall: Popup mit warranties_detail öffnen
            const newBtn = document.getElementById('newWarranty');
            const warrantyDetail = document.getElementById('warrantyDetail');
            if (newBtn && warrantyDetail && warrantyModal) {
                newBtn.addEventListener('click', async (e) => {
                    e.preventDefault();
                    try {
                        const tpl = await MustacheService.getRenderedTemplate('/layout/frontend/beturbo/templates/warranties', 'warranties_detail.mustache', {
                            data: {},
                            newWarranty : true,
                            tc: tcs
                        });
                        warrantyDetail.innerHTML = '';
                        warrantyDetail.append(...tpl);

                        initInputBody();
                        showModal(warrantyModal);
                        saveWarranty(false);
                    } catch (err) {
                        console.error(err);
                    }
                });
            }
        }
    }
}

async function getSingleWarranty() {
    const warrantyDetail = document.getElementById("warrantyDetail");
    const warrantyModal = document.getElementById("warrantyModal");

    if(warrantyDetail && warrantyModal) {
        const warrantyButtons = document.querySelectorAll(".warrantyButton");
        for(const button of warrantyButtons) {
            button.addEventListener("click", async function (event) {
                event.preventDefault();
                const id = this.dataset.id;

                if(id) {
                    const response = await FetchService.get('/em/warranty/getWarranty/' + id);
                    await TextconstantService.preloadTextConstants('bet')
                    const tcs = TextconstantService.getAllTextConstants();


                    if (response.success) {
                        const renderedTemplate = await MustacheService.getRenderedTemplate('/layout/frontend/beturbo/templates/warranties', 'warranties_detail.mustache', {
                            data: response.data,
                            newWarranty : false,
                            tc: tcs
                        })
                        warrantyDetail.innerHTML = "";
                        warrantyDetail.append(...renderedTemplate);

                        const countryInput = document.getElementById("country");
                        if(countryInput) {
                            countryInput.value = response.data.country ?? "DE";
                        }

                        const files = response.data.fileData;

                        if(files != null) {
                            for(const file of files) {
                                const containerId = FILE_TYPE_CONTAINERS[file.type];

                                if(!containerId) {
                                    console.warn('Unbekannter Gewaehrleistungs-Dateityp "' + file.type + '", Datei wird nicht angezeigt:', file.fileName);
                                    continue;
                                }

                                const element = document.getElementById(containerId);

                                if(element) {
                                    element.innerHTML += "<div><a data-path=\"" + file.fullfilePath + "\" data-filename=\"" + file.fileName + "\" href=\"javascript:void(0);\" target=\"__blank\" class=\"warrantyDownload\">" + file.fileName + "</a></div>";
                                }
                            }
                        }


                        initInputBody();
                        showModal(warrantyModal);
                        saveWarranty(response.data.id);
                        downloadPdf();
                    }
                } else {
                    console.log("Keine Id");
                }

            })
        }
    }

}

async function saveWarranty(warrantyId) {
    const warrantyModal = document.getElementById("warrantyModal");
    const button = document.getElementById("saveWarranty");
    if(button && warrantyModal) {
        button.addEventListener("click", async function () {
            try {
                const container = document.querySelector('.warrantyWrap');
                const feedback = document.getElementById('actionFeedback');
                if(!container) return;

                // Validierung: Prüfen, ob alle required Felder ausgefüllt und gültig sind
                // und bei Fehlern das Absenden verhindern sowie Meldung anzeigen
                const requiredFields = container.querySelectorAll('[required]');
                let firstInvalid = null;
                let hasInvalid = false;

                // Vorherige Fehlerzustände zurücksetzen
                requiredFields.forEach((el) => {
                    el.removeAttribute('aria-invalid');
                    const group = el.closest('.inputGroup');
                    if (group) group.classList.remove('has-error');
                });

                requiredFields.forEach((el) => {
                    // HTML5 Constraint Validation nutzen
                    const valid = typeof el.checkValidity === 'function' ? el.checkValidity() : (String(el.value || '').trim() !== '');
                    if (!valid) {
                        hasInvalid = true;
                        if (!firstInvalid) firstInvalid = el;
                        el.setAttribute('aria-invalid', 'true');
                        const group = el.closest('.inputGroup');
                        if (group) group.classList.add('has-error');
                    }
                });


                if (hasInvalid) {
                    if (feedback) {
                        feedback.textContent = (GLOBALS?.tc?.please_fill_required_fields) || 'Bitte füllen Sie alle Pflichtfelder korrekt aus.';
                    }
                    if (firstInvalid && typeof firstInvalid.focus === 'function') {
                        firstInvalid.focus();
                    }
                    return; // API-Call verhindern
                }

                const fields = container.querySelectorAll('input, select, textarea');
                const payload = {};

                fields.forEach((el) => {
                    const element = el;
                    if (element.tagName === 'INPUT') {
                        const type = element.getAttribute('type')?.toLowerCase();
                        if (type === 'file') {
                            return; // skip files
                        }
                        const key = (element.id || element.name || '').trim();
                        if (!key) return;
                        let val;
                        if (type === 'checkbox') {
                            val = element.checked;
                        } else if (type === 'number') {
                            const v = element.value;
                            val = v === '' ? '' : Number(v);
                        } else {
                            val = element.value;
                        }
                        payload[key] = val;
                    } else if (element.tagName === 'SELECT' || element.tagName === 'TEXTAREA') {
                        const key = (element.id || element.name || '').trim();
                        if (!key) return;
                        payload[key] = element.value;
                    }
                });

                if(!warrantyId && !payload.id) {

                    const response = await FetchService.post('/em/warranty/createWarranty/' + document.body.dataset.localeCode, payload);

                    if (response && response.success) {
                        // Nach erfolgreichem Anlegen: Dateien hochladen (falls vorhanden)
                        const createdId = response?.data?.id;
                        if (createdId) {
                            await uploadWarrantyFiles(createdId);
                        }
                        closeModal(warrantyModal);
                        getAllWarranties(1, true, response.data.id);
                    } else {
                        if (feedback) {
                            if(response.errorInfo) {
                                const fields = response.errorInfo;
                                let errorMessage = GLOBALS.tc.missing_fields + ":";
                                for(const fieldname of fields) {
                                    const label = document.querySelector("label[for='" + fieldname + "']");
                                    errorMessage += " " + label.innerHTML.replace(' <span aria-hidden="true">*</span>', '') + ",";
                                }
                                feedback.textContent = errorMessage.replace(/,$/, '');
                            } else {
                                feedback.textContent = GLOBALS.tc.saving_failed;
                            }
                        }
                    }

                } else {
                    if (!warrantyId && payload.id) {
                        warrantyId = payload.id;
                    }

                    if (!warrantyId) {
                        console.error('Keine Warranty-ID gefunden.');
                        if (feedback) {
                            feedback.textContent = GLOBALS.tc.missing_ticket_no;
                        }
                        return;
                    }

                    const response = await FetchService.post('/em/warranty/editWarranty/' + warrantyId, payload);
                    if (response && response.success) {
                        // Nach erfolgreichem Speichern: Dateien hochladen (falls vorhanden)
                        await uploadWarrantyFiles(warrantyId);
                        closeModal(warrantyModal);
                        getAllWarranties(1, true, response.data.id);
                    } else {
                        if (feedback) {
                            console.log(response);
                            if(response.errorInfo) {
                                const fields = response.errorInfo;
                                let errorMessage = GLOBALS.tc.missing_fields + ":";
                                for(const fieldname of fields) {
                                    const label = document.querySelector("label[for='" + fieldname + "']");
                                    errorMessage += " " + label.innerHTML.replace(' <span aria-hidden="true">*</span>', '') + ",";
                                }
                                feedback.textContent = errorMessage.replace(/,$/, '');
                            } else {
                                feedback.textContent = GLOBALS.tc.saving_failed;
                            }
                        }
                    }
                }


            } catch (e) {
                console.error(e);
                const feedback = document.getElementById('actionFeedback');
                if (feedback) {
                    feedback.textContent = GLOBALS.tc.unexpected_error;
                }
            }
        })
    }
}

async function uploadWarrantyFiles(warrantyId) {
    try {
        // Relevante File-Inputs einsammeln
        const inputInvoices = document.getElementById('input_invoices');
        const inputPurchase = document.getElementById('input_purchase');
        const inputImages = document.getElementById('input_images');
        const inputReports = document.getElementById('input_reports');

        const formData = new FormData();

        const appendFiles = (inputEl, keyName) => {
            if (!inputEl || !inputEl.files || inputEl.files.length === 0) return 0;
            let count = 0;
            Array.from(inputEl.files).forEach((file) => {
                if (file) {
                    formData.append(keyName, file, file.name);
                    count++;
                }
            });
            return count;
        };

        const c1 = appendFiles(inputInvoices, 'input_file_invoice');
        const c2 = appendFiles(inputPurchase, 'input_file_purchase_document');
        const c3 = appendFiles(inputImages, 'input_file_pictures');
        const c4 = appendFiles(inputReports, 'input_file_report');

        const total = c1 + c2 + c3 + c4;
        if (total === 0) {
            return; // keine Dateien ausgewählt → kein Upload nötig
        }

        const myHeaders = new Headers();
        myHeaders.append("x-site-code", document.body.dataset.site_code);
        myHeaders.append("x-language-code", document.body.dataset.lang_code);

        const resp = await fetch('/em/warranty/uploadWarrantyFiles/' + encodeURIComponent(String(warrantyId)), {
            method: 'POST',
            body: formData,
            headers: myHeaders,
        });

        try {
            const json = await resp.json();
            if (!json?.success) {
                console.warn('Datei-Upload fehlgeschlagen oder teilweise fehlgeschlagen', json);
            }
        } catch (e) {
            console.warn('Konnte Upload-Antwort nicht parsen.');
        }
    } catch (e) {
        console.error('Fehler beim Hochladen der Dateien:', e);
    }
}

// Make pagination callback accessible for inline onclick handlers
if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-undef
    window.getAllWarranties = getAllWarranties;
}

function downloadPdf() {
    const downloadLinks = document.querySelectorAll(".warrantyDownload");
    for(const download of downloadLinks) {
        download.addEventListener("click", function (e) {
            e.preventDefault();
            const path = this.dataset.path;
            const name = this.dataset.filename;

            fetch(path, {
                method: 'GET',
                headers: {
                    'x-site-code': document.body.dataset.site_code,
                    'x-language-code': document.body.dataset.lang_code
                },
                credentials: 'same-origin' // ensures session cookie is sent
            })
                .then(function(response) {
                    if (!response.ok) throw new Error('Download failed');
                    return response.blob();
                })
                .then(function(blob) {
                    var url = window.URL.createObjectURL(blob);
                    var a = document.createElement('a');
                    a.href = url;
                    a.download = name;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                })
                .catch(function(err) {
                    console.error(err);
                });
        })
    }
}